/**
 * Global "unsaved changes" guard — shared across tenant and admin.
 *
 * Three pieces work together:
 *
 *   1. `<UnsavedChangesProvider>` — mount once near the app root (inside the
 *      locale/confirm providers, OUTSIDE the router). Holds a registry of how
 *      many forms currently have unsaved changes plus the localized copy for
 *      the warning dialog.
 *
 *   2. `useDirtyTracker(value)` (in `useDirtyTracker.ts`) — every form already
 *      uses it. It now auto-registers its `isDirty` signal here, so **no
 *      per-page wiring is needed**: any dirty form automatically arms the
 *      navigation guard. Opt out with
 *      `useDirtyTracker(value, { guardNavigation: false })`.
 *
 *   3. `<UnsavedChangesGuard>` — render once INSIDE the data router (e.g. in
 *      MainLayout). It owns the single `useBlocker` + `beforeunload` listener
 *      and shows the shared confirm dialog when the user tries to leave.
 *
 * `ModalShell` reads the same copy context to confirm closing a dirty modal.
 */
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface UnsavedChangesCopy {
  /** Dialog title. */
  title: string;
  /** Dialog body. */
  message: string;
  /** Confirm (leave / discard) button label. */
  confirmLabel: string;
}

/** Spanish defaults — admin uses these verbatim; tenant overrides with `t()`. */
const DEFAULT_COPY: UnsavedChangesCopy = {
  title: 'Cambios sin guardar',
  message: 'Tienes cambios sin guardar que se perderán si sales. ¿Quieres salir igualmente?',
  confirmLabel: 'Salir sin guardar',
};

export interface UnsavedChangesRegistry {
  /** Add/remove a form's dirty flag by stable id. */
  setDirty: (id: string, dirty: boolean) => void;
  /** True when at least one registered form is dirty. */
  anyDirty: boolean;
  /**
   * Let the *next* in-app navigation through without prompting. Call this right
   * before a programmatic redirect that follows a successful save (e.g. the
   * "create" flow in `SaveButton`), so the guard doesn't false-trigger before
   * the form has had a chance to `markClean()`.
   */
  allowNextNavigation: () => void;
  /** Reads (and clears) the one-shot allow flag. Used by the guard. */
  consumeAllowNextNavigation: () => boolean;
}

const RegistryContext = createContext<UnsavedChangesRegistry | null>(null);
const CopyContext = createContext<UnsavedChangesCopy>(DEFAULT_COPY);

export function UnsavedChangesProvider({
  copy,
  children,
}: {
  /** Localized copy. Memoize it in the caller to avoid needless re-renders. */
  copy?: UnsavedChangesCopy;
  children: ReactNode;
}) {
  const dirtyIds = useRef<Set<string>>(new Set());
  const allowNextRef = useRef(false);
  const [anyDirty, setAnyDirty] = useState(false);

  const setDirty = useCallback((id: string, dirty: boolean) => {
    const set = dirtyIds.current;
    const had = set.has(id);
    if (dirty === had) return;
    if (dirty) set.add(id);
    else set.delete(id);
    setAnyDirty(set.size > 0);
  }, []);

  const allowNextNavigation = useCallback(() => {
    allowNextRef.current = true;
  }, []);

  const consumeAllowNextNavigation = useCallback(() => {
    if (!allowNextRef.current) return false;
    allowNextRef.current = false;
    return true;
  }, []);

  const registry = useMemo<UnsavedChangesRegistry>(
    () => ({ setDirty, anyDirty, allowNextNavigation, consumeAllowNextNavigation }),
    [setDirty, anyDirty, allowNextNavigation, consumeAllowNextNavigation],
  );
  const resolvedCopy = copy ?? DEFAULT_COPY;

  return (
    <RegistryContext.Provider value={registry}>
      <CopyContext.Provider value={resolvedCopy}>{children}</CopyContext.Provider>
    </RegistryContext.Provider>
  );
}

/** Returns the registry, or `null` if no provider is mounted. */
export function useUnsavedChangesRegistry(): UnsavedChangesRegistry | null {
  return useContext(RegistryContext);
}

/** Returns the warning copy (falls back to Spanish defaults). Never throws. */
export function useUnsavedChangesCopy(): UnsavedChangesCopy {
  return useContext(CopyContext);
}

/**
 * Registers `when` into the global guard for as long as the component is
 * mounted. Called by `useDirtyTracker`; rarely needed directly. No-op when no
 * `<UnsavedChangesProvider>` is present (e.g. the web repo, which forked
 * `@shared`).
 */
export function useRegisterUnsavedChanges(when: boolean, enabled = true) {
  const registry = useContext(RegistryContext);
  const id = useId();

  useEffect(() => {
    if (!registry || !enabled) return;
    registry.setDirty(id, when);
    return () => registry.setDirty(id, false);
  }, [registry, id, when, enabled]);
}
